import type { MintedToken } from "./token-minter";

const STORAGE_PREFIX = "secops:mint-kit-session:";

export interface RouteSessionKit {
  routeName: string;
  tokens: MintedToken[];
}

export interface SessionKitLedger {
  byCheckpoint: Record<string, MintedToken>;
  routeKits: Record<string, RouteSessionKit>;
}

export function emptySessionKitLedger(): SessionKitLedger {
  return { byCheckpoint: {}, routeKits: {} };
}

export function ledgerStorageKey(accountId: string): string {
  return `${STORAGE_PREFIX}${accountId}`;
}

function isMintedToken(value: unknown): value is MintedToken {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.checkpointId === "string" &&
    row.checkpointId.length > 0 &&
    typeof row.checkpointName === "string" &&
    typeof row.token === "string" &&
    row.token.length > 0
  );
}

function parseLedger(raw: unknown): SessionKitLedger {
  if (typeof raw !== "object" || raw === null) return emptySessionKitLedger();
  const value = raw as Record<string, unknown>;
  const byCheckpoint: Record<string, MintedToken> = {};
  const routeKits: Record<string, RouteSessionKit> = {};

  if (typeof value.byCheckpoint === "object" && value.byCheckpoint !== null) {
    for (const [checkpointId, token] of Object.entries(value.byCheckpoint as Record<string, unknown>)) {
      if (isMintedToken(token)) {
        byCheckpoint[checkpointId] = token;
      }
    }
  }

  if (typeof value.routeKits === "object" && value.routeKits !== null) {
    for (const [routeId, entry] of Object.entries(value.routeKits as Record<string, unknown>)) {
      if (typeof entry !== "object" || entry === null) continue;
      const kit = entry as Record<string, unknown>;
      const routeName = typeof kit.routeName === "string" ? kit.routeName : "Route";
      const tokens = Array.isArray(kit.tokens) ? kit.tokens.filter(isMintedToken) : [];
      if (tokens.length > 0) {
        routeKits[routeId] = { routeName, tokens };
      }
    }
  }

  return { byCheckpoint, routeKits };
}

export function readSessionKitLedger(accountId: string): SessionKitLedger {
  try {
    const raw = sessionStorage.getItem(ledgerStorageKey(accountId));
    if (raw === null) return emptySessionKitLedger();
    return parseLedger(JSON.parse(raw));
  } catch {
    return emptySessionKitLedger();
  }
}

export function writeSessionKitLedger(accountId: string, ledger: SessionKitLedger): void {
  try {
    sessionStorage.setItem(ledgerStorageKey(accountId), JSON.stringify(ledger));
  } catch {
    // Private mode or quota: React state still holds the in-tab ledger.
  }
}

export function applyMintToLedger(
  ledger: SessionKitLedger,
  tokens: MintedToken[],
  routeKit?: { routeId: string; routeName: string },
): SessionKitLedger {
  const byCheckpoint = { ...ledger.byCheckpoint };
  for (const token of tokens) {
    if (token.checkpointId.length > 0) {
      byCheckpoint[token.checkpointId] = token;
    }
  }

  const routeKits = { ...ledger.routeKits };
  if (routeKit !== undefined) {
    routeKits[routeKit.routeId] = {
      routeName: routeKit.routeName,
      tokens,
    };
  }

  return { byCheckpoint, routeKits };
}
